import styled from "@emotion/styled";
import { Link } from 'react-router-dom';

export const palette = {
  navy: '#0d1729',
  navyLight: '#1d2a3e',
  navyBorder: '#34445d',
  track: '#0a1426',
  white: '#ffffff',
  text: '#edf2fa',
  muted: '#718099',
  chip: '#344258',
  green: '#22d772',
  orange: '#ff7417',
  red: '#ff3f4d',
  blue: '#6773ff',
};

export const DashboardPage = styled.div`
  min-height: 100vh;
  margin-top: -36px;
  background: ${palette.navy};
  color: ${palette.text};
  font-family: Lato, Arial, Helvetica, sans-serif;

  @media (min-width: 768px) {
    margin: -16px -24px 0 -40px;
  }

  @media (min-width: 1260px) {
    margin-top: -40px;
  }
`;

export const Header = styled.header`
  min-height: 92px;
  padding: 18px clamp(24px, 4vw, 58px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: ${palette.white};
  border-bottom: 3px solid ${palette.red};
  color: #1a385f;

  @media (max-width: 680px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
`;

export const Logo = styled.img`
  display: block;
  width: 99px;
  height: 50px;
  object-fit: contain;
`;

export const HeadingGroup = styled.div`
  text-align: right;

  h1 {
    margin: 0;
    font-size: clamp(18px, 2.1vw, 26px);
    font-weight: 900;
    letter-spacing: 0.6px;
  }

  p {
    margin: 7px 0 0;
    color: #8b96a7;
    font-size: 11px;
  }

  @media (max-width: 680px) {
    text-align: left;
  }
`;

export const Content = styled.main`
  width: min(1200px, calc(100% - 40px));
  margin: 0 auto;
  padding: 30px 0 66px;
`;

export const SummaryRow = styled.div`
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
`;

export const SortNote = styled.div`
  color: ${palette.muted};
  font-size: 14px;

  &::before {
    content: '↑';
    margin-right: 5px;
  }
`;

export const Metrics = styled.div`
  display: flex;
  gap: 10px;
`;

export const MetricCard = styled.div`
  min-width: 105px;
  padding: 12px 14px 10px;
  text-align: center;
  border: 1px solid ${palette.navyBorder};
  border-radius: 11px;
  background: ${palette.navyLight};

  strong {
    display: block;
    color: ${palette.green};
    font-size: 22px;
    line-height: 1;
  }

  &:last-child strong {
    color: ${palette.blue};
  }

  span {
    display: block;
    margin-top: 7px;
    color: ${palette.muted};
    font-size: 13px;
  }
`;

export const TeamList = styled.div`
  display: grid;
  gap: 12px;
`;

export const MemberCard = styled.article`
  padding: 19px 22px 17px;
  border: 1px solid ${palette.navyBorder};
  border-radius: 15px;
  background: ${palette.navyLight};
`;

export const MemberTop = styled.div`
  display: grid;
  grid-template-columns: 245px minmax(220px, 1fr) 72px;
  align-items: start;
  gap: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr 64px;
  }
`;

export const Identity = styled.div`
  display: flex;
  align-items: center;
`;

export const MemberName = styled(Link)`
  color: ${palette.text};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${palette.blue};
    text-decoration: underline;
  }
`;

export const Capacity = styled.div`
  min-width: 0;

  @media (max-width: 760px) {
    grid-column: 1 / -1;
    grid-row: 2;
  }
`;

export const CapacityLabels = styled.div`
  margin-bottom: 7px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: ${palette.muted};
  font-size: 13px;
`;

export const CapacityEditor = styled.form`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const CapacityInput = styled.input`
  width: 58px;
  padding: 3px 5px;
  border: 1px solid ${palette.navyBorder};
  border-radius: 4px;
  background: ${palette.track};
  color: ${palette.text};
  font: inherit;
  text-align: right;
`;

export const CapacityButton = styled.button`
  padding: 3px 7px;
  border: 1px solid ${palette.navyBorder};
  border-radius: 4px;
  background: ${palette.chip};
  color: ${palette.text};
  cursor: pointer;
  font: inherit;
  font-size: 11px;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

export const CapacityLimitButton = styled(CapacityButton)`
  margin-left: 6px;
`;

export const CapacityError = styled.div`
  margin-top: -3px;
  color: ${palette.red};
  font-size: 11px;
  text-align: right;
`;

export const Track = styled.div`
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: ${palette.track};
`;

export const Fill = styled.div<{ percent: number; fillColor: string }>`
  width: ${(props) => Math.min(props.percent, 100)}%;
  height: 100%;
  border-radius: inherit;
  background: ${(props) => props.fillColor};
`;

export const CapacityPercent = styled.div`
  margin-top: 6px;
  color: ${palette.muted};
  font-size: 12px;
`;

export const SlotsLeft = styled.div<{ statusColor: string }>`
  padding: 9px 5px 8px;
  text-align: center;
  border: 2px solid ${(props) => props.statusColor};
  border-radius: 11px;
  color: ${(props) => props.statusColor};

  strong {
    display: block;
    font-size: 25px;
    line-height: 1;
  }

  span {
    display: block;
    margin-top: 6px;
    color: ${palette.muted};
    font-size: 13px;
  }
`;

export const WorkloadChips = styled.div`
  margin-top: 18px;
  padding-top: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid #101b2d;
`;

export const Chip = styled.div<{ dimmed: boolean }>`
  padding: 9px 13px;
  border: 1px solid #4a5a72;
  border-radius: 11px;
  background: ${(props) => (props.dimmed ? 'transparent' : palette.chip)};
  color: ${(props) => (props.dimmed ? '#56647a' : '#cbd4e2')};
  font-size: 14px;
  line-height: 1.1;

  span {
    display: inline-block;
    margin-right: 7px;
    font-size: 17px;
    line-height: 1;
    vertical-align: -1px;
  }

  strong {
    color: ${(props) => (props.dimmed ? '#56647a' : palette.white)};
  }
`;

export const Legend = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 22px;
  color: ${palette.muted};
  font-size: 13px;
`;

export const LegendItem = styled.span<{ color: string }>`
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 6px;
    border-radius: 50%;
    background: ${(props) => props.color};
  }
`;
